import React, { useEffect, useMemo, useState, useRef } from "react";
import MiniVBarChart from "./MiniVBarChart";
import { useContainerWidth } from "src/components/Hooks/useContainerWidth";
import { useContainerHeight } from "src/components/Hooks/useContainerHeight";
import { calculateYAxisDomain, extractAllValues, formatDataLabel, getHeaderTextStyle, hasLowNSize } from "../chartHelpers";
import { ChartContainer } from "../../components/ChartContainer";
import { ChartFootnote } from "../../components/ChartFootnote";
import ChartHeader from "../../components/ChartHeader";
import { EllipsisText } from "../../components/EllipsisText";
import { NSizeLabel } from "../../components/NSizeLabel";
import { YAxisScale } from "../../components/YAxisScale";
import { chartConfig } from "../../chartConfig";
import ChartTabularView from "../../components/ChartTabularView";
import { ChartRenderedFromType, IHierarchyDef, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { useScaledConfig } from "../../ChartScaleContext";
import { pxToRem } from "src/pages/Insights/transformer/dashboardUtils";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { AxisLabel } from "../../components/AxisLabel";
import ChartError from "../../ChartError";

/* -------------------- Types -------------------- */
type FlatColumn = {
	path: string[];
	attribute: string;
	n_size?: { min: number; max: number } | number;
	seriesData: any[];
};

type HeaderCell = {
	label: string;
	colSpan: number;
	levelIndex: number;
};

interface VClusteredBarChartProps {
	chartResponse: any;
	chartRules?: Record<string, any>;
	isAISummaryAvailable?: boolean;
	isChartView?: boolean;
	setIsChartView?: (value: boolean) => void;
	renderedFrom?: ChartRenderedFromType;
}

// Add after the HeaderCell type definition (around line 22)

type CellType = "header" | "nsize" | "attrLabel" | "data";

interface ICellPosition {
	rowIndex: number;
	colIndex: number;
	totalCols: number;
}

interface IBorderConfig {
	borderTop?: string;
	borderBottom?: string;
	borderLeft?: string;
	borderRight?: string;
}

/* -------------------- Border Helpers -------------------- */
const createBorderConfig = (colorsConfig: any) => {
	const solidBorder = `1px solid ${colorsConfig.borders.solid}`;
	const dashedBorder = `1px dashed ${colorsConfig.borders.solid}`;

	return {
		solid: solidBorder,
		dashed: dashedBorder,
	};
};

const getCellBorders = (
	cellType: CellType,
	position: ICellPosition,
	borderConfig: { solid: string; dashed: string },
	columns: FlatColumn[],
	hierarchyLength: number,
): IBorderConfig => {
	const { rowIndex, colIndex, totalCols } = position;
	const isLastCol = colIndex === totalCols - 1;
	const isFirstCol = colIndex === 0;

	const getLeftBorder = (): string | undefined => {
		if (colIndex === 0) return undefined;

		const col = columns[colIndex];
		const prevCol = columns[colIndex - 1];

		if (!col?.path?.length || !prevCol?.path?.length) return undefined;

		for (let level = 0; level < col.path.length; level++) {
			if (col.path[level] !== prevCol.path[level]) {
				return level < 2 ? borderConfig.solid : borderConfig.dashed;
			}
		}

		return undefined;
	};

	const getRightBorder = (): string | undefined => {
		if (isLastCol) return undefined;

		const col = columns[colIndex];
		const nextCol = columns[colIndex + 1];

		if (!col?.path?.length || !nextCol?.path?.length) return undefined;

		for (let level = 0; level < col.path.length; level++) {
			if (col.path[level] !== nextCol.path[level]) {
				return level < 2 ? borderConfig.solid : borderConfig.dashed;
			}
		}

		return undefined;
	};

	switch (cellType) {
		case "header":
			return {
				borderTop: borderConfig.solid,
				borderRight: isLastCol ? undefined : borderConfig.solid,
			};

		case "attrLabel":
			return {
				borderTop: borderConfig.solid,
				borderRight: isLastCol ? undefined : borderConfig.solid,
				// borderLeft: getLeftBorder(),
			};

		case "nsize":
			return {
				borderTop: rowIndex === 0 ? borderConfig.solid : undefined,
				borderBottom: borderConfig.solid,
				borderRight: isLastCol ? undefined : borderConfig.solid,
				// borderLeft: getLeftBorder(),
			};

		case "data":
			return {
				borderBottom: borderConfig.solid,
				borderRight: getRightBorder(),
			};

		default:
			return {};
	}
};

const useBorderGetter = (colorsConfig: any, columns: FlatColumn[], hierarchyLength: number) => {
	const cacheRef = useRef<Map<string, IBorderConfig>>(new Map());
	const borderConfigRef = useRef(createBorderConfig(colorsConfig));

	useEffect(() => {
		borderConfigRef.current = createBorderConfig(colorsConfig);
		cacheRef.current.clear();
	}, [colorsConfig, columns]);

	const getBorders = React.useCallback(
		(cellType: CellType, rowIndex: number, colIndex: number): IBorderConfig => {
			const key = `${cellType}:${rowIndex}:${colIndex}`;

			const cached = cacheRef.current.get(key);
			if (cached !== undefined) {
				return cached;
			}

			const position: ICellPosition = {
				rowIndex,
				colIndex,
				totalCols: columns.length,
			};

			const borders = getCellBorders(cellType, position, borderConfigRef.current, columns, hierarchyLength);

			cacheRef.current.set(key, borders);

			return borders;
		},
		[columns, hierarchyLength],
	);

	return getBorders;
};

/* -------------------- Authoritative Order Resolver -------------------- */
/**
 * Reorder discovered keys at a given 0-based nesting depth using the
 * authoritative values from filter_hierarchy. Hierarchies are sorted by
 * `order` ascending and selected by ordinal position. Stragglers
 * (keys not in the hierarchy def) are appended in original discovery order.
 */
const resolveKeysByHierarchyOrder = (
	discoveredKeys: string[],
	filterHierarchy: IHierarchyDef[] | undefined,
	depth: number,
): string[] => {
	if (!filterHierarchy || filterHierarchy.length === 0) return discoveredKeys;

	const sortedHierarchies = [...filterHierarchy].sort((a, b) => a.order - b.order);
	const hierarchyDef = sortedHierarchies[depth];

	if (!hierarchyDef || !Array.isArray(hierarchyDef.values) || hierarchyDef.values.length === 0) {
		return discoveredKeys;
	}

	const discoveredSet = new Set(discoveredKeys);
	const ordered: string[] = [];

	hierarchyDef.values.forEach((val) => {
		if (discoveredSet.has(val)) {
			ordered.push(val);
			discoveredSet.delete(val);
		}
	});

	discoveredKeys.forEach((k) => {
		if (discoveredSet.has(k)) ordered.push(k);
	});

	return ordered;
};

/* -------------------- Logic Helpers -------------------- */
const buildFlatAttributeColumns = (
	dataNode: any,
	filterHierarchy: IHierarchyDef[] | undefined,
	currentPath: string[] = [],
): FlatColumn[] => {
	if (Array.isArray(dataNode)) {
		// Reorder leaf array items by xAxis using filter_hierarchy at this depth.
		// xAxis sits at depth = currentPath.length (e.g. answer_value at depth 2 for Country/Specialty).
		const xAxisDepth = currentPath.length;
		const xAxisValues = dataNode.map((row: any) => row.xAxis);
		const orderedXAxis = resolveKeysByHierarchyOrder(xAxisValues, filterHierarchy, xAxisDepth);
		const rowMap = new Map<string, any>(dataNode.map((row: any) => [row.xAxis, row]));

		return orderedXAxis
			.map((xAxis) => {
				const row = rowMap.get(xAxis);
				if (!row) return null;
				return {
					path: currentPath,
					attribute: row.xAxis,
					n_size: typeof row.n_size === "number" ? { min: row.n_size, max: row.n_size } : row.n_size,
					seriesData: row.series,
				};
			})
			.filter(Boolean) as FlatColumn[];
	}

	let cols: FlatColumn[] = [];
	const orderedKeys = resolveKeysByHierarchyOrder(Object.keys(dataNode), filterHierarchy, currentPath.length);
	orderedKeys.forEach((key) => {
		cols = cols.concat(buildFlatAttributeColumns(dataNode[key], filterHierarchy, [...currentPath, key]));
	});
	return cols;
};

const buildAtomicHeaderRow = (columns: FlatColumn[], levelIndex: number): HeaderCell[] => {
	const headers: HeaderCell[] = [];
	if (columns.length === 0) return headers;

	let currentLabel = columns[0].path[levelIndex];
	let currentSpan = 0;
	let prevCol: FlatColumn | null = null;

	columns.forEach((col, i) => {
		const label = col.path[levelIndex];
		let parentChanged = false;

		if (levelIndex > 0 && prevCol) {
			for (let p = 0; p < levelIndex; p++) {
				if (col.path[p] !== prevCol.path[p]) {
					parentChanged = true;
					break;
				}
			}
		}

		if (i > 0 && (label !== currentLabel || parentChanged)) {
			headers.push({ label: currentLabel, colSpan: currentSpan, levelIndex });
			currentLabel = label;
			currentSpan = 1;
		} else {
			currentSpan++;
		}
		prevCol = col;
	});

	headers.push({ label: currentLabel, colSpan: currentSpan, levelIndex });
	return headers;
};

/* -------------------- Main Component -------------------- */
const VClusteredBarChart: React.FC<VClusteredBarChartProps> = ({ chartResponse, chartRules, isAISummaryAvailable = false, isChartView, setIsChartView, renderedFrom }) => {
	/* Existing config reads — unchanged */
	const layoutConfig = chartConfig.getLayoutConfig();
	const dimensionsConfig = chartConfig.getDimensionsConfig();
	const spacingConfig = chartConfig.getSpacingConfig();
	const vClusteredConfig = chartConfig.getVerticalClusteredConfig();
	const typographyConfig = chartConfig.getTypographyConfig();
	const colorsConfig = chartConfig.getColorsConfig();
	const SCROLLBAR_HEIGHT = 6;

	/* ← SCALED: read ScaledConfig from context.
		 In single-chart mode this returns IDENTITY_SCALE (scaleFactor = 1),
		 so every scaled.X equals the existing base — zero behavioural change. */
	const scaled = useScaledConfig();
	const isRenderedFromAskKural = renderedFrom === ChartRenderedFromType.AskKural;
	const isRenderedFromAskKuralInline = renderedFrom === ChartRenderedFromType.AskKuralInline;
	const { isMultiTileSlide } = useSelector((state: RootState) => state.quantDashboardSlice);
	const isEffectiveMultiTile = isMultiTileSlide || isRenderedFromAskKuralInline;

	const apiData = chartResponse?.data?.[0]?.chart_data;
	// Determines if chart_data is missing/empty/invalid to render fallback UI
	const isDataEmpty = useMemo(() => {
		if (apiData === null || apiData === undefined) return true;
		if (Array.isArray(apiData) && apiData.length === 0) return true;
		if (typeof apiData !== "object") return true; // guard against primitives
		if (!Array.isArray(apiData) && Object.keys(apiData).length === 0) return true;
		return false;
	}, [apiData]);
	const hierarchyDef = chartResponse?.filter_hierarchy || [];
	const hasHierarchy = hierarchyDef.length > 0;
	// Use optional chaining — chart_config itself may be missing
	const labelFormat = chartResponse?.chart_config?.dlabel_fmt;
	const statTestConfig: IStatTestConfig | null = chartResponse?.chart_config?.stat_test_config ?? null;

	const [visibleWaves, setVisibleWaves] = useState<string[]>([]);
	const [expanded, setExpanded] = useState(false);
	const [recalcKey, setRecalcKey] = useState(0);

	const containerRef = useRef<HTMLDivElement>(null);
	const contentAreaRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const footnoteRef = useRef<HTMLDivElement>(null);
	const tableHeaderRef = useRef<HTMLDivElement>(null);
	const tableViewContainerRef = useRef<HTMLDivElement>(null);

	const containerWidth = useContainerWidth(containerRef, recalcKey);
	// NEW — stabilize height to break the resize observer feedback loop.
	// Only updates when the measured height changes by more than 5px,
	// preventing the 1px-per-frame shrink caused by chart height
	// feeding back into the container it's measured from.
	// NEW — two-tier stabilization: tight lock for AskKural (feedback-prone),
	// standard 5px threshold for dashboard (external resizes expected)
	// Reusable stabilized height pattern — add to HClusteredBarChart, HStackedBarChart,
	// VStackedBarChart, HLineChart, VLineChart
	const rawContentAreaHeight = useContainerHeight(contentAreaRef, recalcKey);
	const stableContentAreaHeightRef = useRef<number>(0);
	const measureCountRef = useRef<number>(0);

	const contentAreaHeight = useMemo(() => {
		const raw = rawContentAreaHeight || 0;
		const prev = stableContentAreaHeightRef.current;

		// Never lock on zero — wait for a real measurement
		if (raw === 0) return prev;

		if (prev === 0) {
			stableContentAreaHeightRef.current = raw;
			measureCountRef.current = 1;
			return raw;
		}

		measureCountRef.current += 1;

		// Lock after 2 real (non-zero) measurements for AskKuralInline to prevent oscillation
		if (isRenderedFromAskKuralInline && measureCountRef.current > 2) {
			return prev;
		}

		// Standard threshold for dashboard
		if (Math.abs(raw - prev) > 5) {
			stableContentAreaHeightRef.current = raw;
			return raw;
		}

		return prev;
	}, [rawContentAreaHeight, isRenderedFromAskKuralInline]);

	const tableViewContainerHeight = useContainerHeight(tableViewContainerRef, recalcKey) || contentAreaHeight;
	const rawTableHeaderHeight = useContainerHeight(tableHeaderRef, recalcKey);
	const stableTableHeaderHeightRef = useRef<number>(0);

	/**
	 * Use the maximum observed table header height to prevent oscillation.
	 * When text wraps in narrow columns (e.g. attribute "Dupixent (dupilumab)" at ~80px),
	 * the header height inflates. If we then shrink the chart, a re-layout
	 * may unwrap the text, reducing header height, which grows the chart,
	 * which re-wraps — causing an infinite loop.
	 * Taking the max ensures the chart always reserves enough space for the
	 * tallest header measurement, allowing wrapping without overflow.
	 */
	const tableHeaderHeight = useMemo(() => {
		const raw = rawTableHeaderHeight || 0;
		if (raw > stableTableHeaderHeightRef.current) {
			stableTableHeaderHeightRef.current = raw;
		}
		return stableTableHeaderHeightRef.current;
	}, [rawTableHeaderHeight]);


	useEffect(() => {
		// Guard against missing ser_color to avoid Object.keys(undefined) crash
		setVisibleWaves(Object.keys(chartResponse?.chart_config?.ser_color ?? {}));
	}, [chartResponse?.chart_config?.ser_color]);

	useEffect(() => {
		if (isChartView) {
			// Reset high-water-mark on view switch so fresh measurements are taken
			stableTableHeaderHeightRef.current = 0;
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
			setRecalcKey((p) => p + 1);
		}
	}, [isChartView]);

	// AskKuralInline mounts inside a still-settling flex chain; the initial
	// ResizeObserver tick can read offsetHeight=0 and never recover, leaving the
	// chart in its placeholder state. Force one more re-measure after the first
	// paint so we capture the settled size.
	useEffect(() => {
		if (!isRenderedFromAskKuralInline) return;
		const id = requestAnimationFrame(() => {
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
			setRecalcKey((p) => p + 1);
		});
		return () => cancelAnimationFrame(id);
	}, [isRenderedFromAskKuralInline]);

	// One-shot recalc once the table header first reports a real height.
	// On initial paint, the header ref hasn't been measured yet, so
	// dynamicChartHeight is computed against a zero header and the bars
	// render at the wrong scale. Reset the contentArea stabilization and
	// bump recalcKey so every ref re-measures against the settled layout.
	const didRecalcAfterHeaderRef = useRef(false);
	useEffect(() => {
		if (didRecalcAfterHeaderRef.current) return;
		if (rawTableHeaderHeight > 0) {
			didRecalcAfterHeaderRef.current = true;
			stableContentAreaHeightRef.current = 0;
			measureCountRef.current = 0;
			setRecalcKey((p) => p + 1);
		}
	}, [rawTableHeaderHeight]);

	// Skip flattening when data is empty to avoid runtime errors
	const flatColumns = useMemo(
		() => (isDataEmpty ? [] : buildFlatAttributeColumns(apiData, hierarchyDef)),
		[apiData, hierarchyDef, isDataEmpty],
	);

	// Authoritative series order from chart_config.ser_color (consumed by ChartTabularView)
	const seriesOrder = useMemo(
		() => (chartResponse?.chart_config?.ser_color ? Object.keys(chartResponse.chart_config.ser_color) : []),
		[chartResponse?.chart_config?.ser_color],
	);

	// Skip hasLowNSize when data is empty — it may not handle undefined/empty internally
	const showLowNSize = useMemo(() => (isDataEmpty ? false : hasLowNSize(apiData, { threshold: 30 })), [apiData, isDataEmpty]);

	const maxVisibleSeries = useMemo(() => {
		if (!visibleWaves || !flatColumns.length) return 1;
		return Math.max(...flatColumns.map((col) => col.seriesData.filter((s: any) => visibleWaves?.includes(s.name)).length), 1);
	}, [flatColumns, visibleWaves]);

	const isCompactMode = flatColumns.length >= dimensionsConfig.column.compactThreshold;
	const isUltraCompact = isCompactMode && maxVisibleSeries <= dimensionsConfig.column.ultraCompactBarThreshold;

	const yAxisDomain = useMemo(() => {
		return calculateYAxisDomain(extractAllValues(flatColumns), labelFormat);
	}, [flatColumns, labelFormat]);

	const minColumnWidth = useMemo(() => {
		const baseMin = isUltraCompact ? dimensionsConfig.column.minWidthUltraCompact : isCompactMode ? dimensionsConfig.column.minWidthCompact : dimensionsConfig.column.minWidth;
		const bw = isUltraCompact ? vClusteredConfig.clusterBarWidthUltraCompact : isCompactMode ? vClusteredConfig.clusterBarWidthCompact : vClusteredConfig.clusterBarWidth;
		return Math.max(baseMin, maxVisibleSeries * bw + (maxVisibleSeries - 1) * vClusteredConfig.barGap + 12);
	}, [isCompactMode, isUltraCompact, dimensionsConfig.column, vClusteredConfig, maxVisibleSeries]);

	const scrollColumnWidth = useMemo(() => {
		if (!isCompactMode) return minColumnWidth;
		return Math.ceil(minColumnWidth * 1.75);
	}, [isCompactMode, minColumnWidth]);

	/* ← SCALED: axisColumnWidth uses scaled value instead of hardcoded 45 */
	const showAxisColumn = !isEffectiveMultiTile || isRenderedFromAskKural;

	const axisColWidth = showAxisColumn ? scaled.geometry.axisColumnWidth : 0;
	// AxisLabel (the rotated "y_axis_label" text) takes a fixed 20px beside the chart area
	const axisLabelWidth = isChartView && chartResponse?.y_axis_label && !isEffectiveMultiTile ? 20 : 0;

	const [actualContainerPaddingH, setActualContainerPaddingH] = useState(2 * scaled.layout.containerPadding);
	useEffect(() => {
		if (!containerRef.current) return;
		const s = getComputedStyle(containerRef.current);
		setActualContainerPaddingH((parseFloat(s.paddingLeft) || 0) + (parseFloat(s.paddingRight) || 0));
	}, [scaled.layout.containerPadding]);

	const scrollableBorderPx = isEffectiveMultiTile && !isRenderedFromAskKural ? 2 : 1;

	const needsScroll = useMemo(() => {
		if (containerWidth === 0) return false;
		return axisColWidth + axisLabelWidth + actualContainerPaddingH + scrollableBorderPx + flatColumns.length * scrollColumnWidth > containerWidth;
	}, [containerWidth, flatColumns.length, scrollColumnWidth, axisColWidth, axisLabelWidth, actualContainerPaddingH, scrollableBorderPx]);

	// Uses exact inner-div pixel width derived from containerWidth and truly-rendered values.
	const actualColumnWidth = useMemo(() => {
		if (containerWidth === 0) return scrollColumnWidth;
		if (needsScroll) return scrollColumnWidth;
		const availableWidth = containerWidth - actualContainerPaddingH - axisLabelWidth - scrollableBorderPx - axisColWidth;
		return Math.floor(availableWidth / flatColumns.length);
	}, [containerWidth, needsScroll, scrollColumnWidth, flatColumns.length, axisColWidth, axisLabelWidth, actualContainerPaddingH, scrollableBorderPx]);

	const barWidth = useMemo(() => {
		const baseWidth = isUltraCompact
			? vClusteredConfig.clusterBarWidthUltraCompact
			: isCompactMode
				? vClusteredConfig.clusterBarWidthCompact
				: vClusteredConfig.clusterBarWidth;

		/* ← SCALED: cap effective max bar width by scaled.geometry.maxBarWidth */
		const scaledMaxSingle = Math.min(vClusteredConfig.maxBarWidthSingle, scaled.geometry.maxBarWidth);
		const scaledMax = Math.min(vClusteredConfig.maxBarWidth, scaled.geometry.maxBarWidth);
		const effectiveMax = maxVisibleSeries === 1 ? scaledMaxSingle : scaledMax;

		if (!isCompactMode && actualColumnWidth > 0) {
			const calc = Math.floor((actualColumnWidth - 12) / maxVisibleSeries);
			return Math.max(baseWidth, Math.min(Math.max(calc, vClusteredConfig.minBarWidth), effectiveMax));
		}
		if (maxVisibleSeries === 1 && actualColumnWidth > 0) {
			const pct = Math.floor(actualColumnWidth * vClusteredConfig.singleBarWidthPercent);
			return Math.max(baseWidth, Math.min(Math.max(pct, vClusteredConfig.minBarWidth), scaledMaxSingle));
		}
		if (maxVisibleSeries >= 2 && maxVisibleSeries <= 3 && actualColumnWidth > 0) {
			const calc = Math.floor((actualColumnWidth - 12) / maxVisibleSeries);
			return Math.max(baseWidth, Math.min(Math.max(calc, vClusteredConfig.minBarWidth), scaledMax));
		}
		return Math.min(baseWidth, scaled.geometry.maxBarWidth);
	}, [isUltraCompact, isCompactMode, vClusteredConfig, maxVisibleSeries, actualColumnWidth, scaled.geometry.maxBarWidth]);

	/* ← SCALED: data label font size uses scaled typography */
	const dataLabelFontSize = isCompactMode ? Math.min(typographyConfig.fontSize.dataLabelCompact, scaled.typography.dataLabel) : scaled.typography.dataLabel;
	// NEW — cap for AskKural
	const ASK_KURAL_MAX_CHART_HEIGHT = 150;

	const dynamicChartHeight = useMemo(() => {
		if (contentAreaHeight === 0) {
			return isRenderedFromAskKuralInline ? ASK_KURAL_MAX_CHART_HEIGHT : dimensionsConfig.chart.defaultHeight;
		}

		const resolvedHeaderHeight = tableHeaderHeight || 0;
		const scrollbarOffset = needsScroll ? SCROLLBAR_HEIGHT : 0.5;
		const availableHeight = contentAreaHeight - resolvedHeaderHeight - scrollbarOffset;

		if (availableHeight < dimensionsConfig.chart.minHeight) {
			return Math.max(0, availableHeight);
		}

		const maxHeight = isRenderedFromAskKuralInline
			? ASK_KURAL_MAX_CHART_HEIGHT
			: dimensionsConfig.chart.maxHeight;

		return Math.min(maxHeight, availableHeight);
	}, [contentAreaHeight, tableHeaderHeight, dimensionsConfig.chart, needsScroll, isRenderedFromAskKuralInline]);

	const gridTemplate = useMemo(() => {
		const axisCol = showAxisColumn ? `${axisColWidth}px ` : "";
		if (needsScroll) return `${axisCol}repeat(${flatColumns.length}, ${scrollColumnWidth}px)`;
		return `${axisCol}repeat(${flatColumns.length}, 1fr)`;
	}, [needsScroll, axisColWidth, flatColumns.length, scrollColumnWidth, showAxisColumn]);

	// Reserve space beneath the chart only when the y-domain includes negatives,
	// so labels on downward bars don't collide with the x-axis/footer.
	const chartBottomPadding = yAxisDomain.min < 0
		? (needsScroll
			? layoutConfig.axis.bottom + spacingConfig.padding.buffer / 2
			: layoutConfig.axis.bottom + spacingConfig.padding.buffer / 3)
		: 0;

	const getBorders = useBorderGetter(colorsConfig, flatColumns, hierarchyDef.length);

	if (isDataEmpty) {
		return <ChartError errorMessage="No data available" setChartData={() => { }} setIsChartLoading={() => { }} is_retry={false} />;
	}

	return (
		/* ← SCALED: containerPadding replaces hardcoded p-4 */
		<ChartContainer innerRef={containerRef} scaledPadding={scaled.layout.containerPadding} renderedFrom={renderedFrom}>
			<div ref={headerRef}>
				<ChartHeader
					title={chartResponse.chart_title}
					ai_header={chartResponse?.ai_header}
					subtitle={chartResponse.chart_subtitle}
					legendWaves={chartResponse?.chart_config?.ser_color}
					visibleWaves={visibleWaves}
					setVisibleWaves={setVisibleWaves}
					expanded={expanded}
					setExpanded={setExpanded}
					layout="center"
					isAISummaryAvailable={isAISummaryAvailable}
					isChartView={isChartView}
					setIsChartView={setIsChartView}
					/* ← SCALED: new optional props */
					showToggle={scaled.layout.showViewToggle}
					scaledTitleFontSize={pxToRem(scaled.typography.title)}
					scaledLegendFontSize={scaled.typography.legend}
					showSubtitle={scaled.layout.showSubtitle}
					showLegend={scaled.layout.showLegend}
					legendLines={scaled.layout.legendLines}
					renderedFrom={renderedFrom}
				/>
			</div>

			{isChartView ? (
				<div ref={contentAreaRef} className="flex flex-1 min-h-0 overflow-hidden relative">
					{contentAreaHeight > 0 || isRenderedFromAskKuralInline ? (
						<>
							{/* Y-Axis Label */}
							{isChartView && chartResponse?.y_axis_label && !isEffectiveMultiTile && (
								<AxisLabel
									label={chartResponse?.y_axis_label || chartResponse?.chart_config?.y_lbl || "Attributes"}
									orientation="vertical"
									maxHeight={contentAreaHeight || undefined}
								/>
							)}
					<div
						className={`h-full w-full ${needsScroll ? "overflow-x-auto" : "overflow-x-hidden"} overflow-y-hidden`}
						style={{ borderRight: `1px solid ${colorsConfig.borders.solid}`, ...(isEffectiveMultiTile && !isRenderedFromAskKural && { borderLeft: `1px solid ${colorsConfig.borders.solid}` }) }}>
							<div className={needsScroll ? "inline-block min-w-full" : "w-full"}>
									<div ref={tableHeaderRef}>
										{/* ── Hierarchy headers ─────────────────────────────────── */}
										{hasHierarchy &&
											hierarchyDef.map((_levelDef: any, levelIndex: number) => {
												const headers = buildAtomicHeaderRow(flatColumns, levelIndex);
												const isLastLevel = levelIndex === hierarchyDef.length - 1;
												return (
													<div key={levelIndex} className="grid" style={{ gridTemplateColumns: gridTemplate }}>
														{showAxisColumn && (
															<div className="sticky left-0 z-20 bg-white" style={{ borderRight: `1px solid ${colorsConfig.borders.solid}` }} />
														)}
														{headers.map((cell, idx) => {
															const textStyle = getHeaderTextStyle(levelIndex, isLastLevel, scaled.scaleFactor);
															return (
																<div
																	key={idx}
																	className={`text-center flex flex-col items-center justify-center ${textStyle.className}`}
																	style={{
																		gridColumn: `span ${cell.colSpan}`,
																		/* ← SCALED: header row height + padding */
																		minHeight: pxToRem(scaled.layout.headerRowHeight),
																		padding: `${scaled.geometry.cellPaddingV}px ${scaled.geometry.cellPaddingH}px`,
																		borderRight: idx === headers.length - 1 ? "none" : `1px solid ${colorsConfig.borders.solid}`,
																		borderTop: `1px solid ${colorsConfig.borders.solid}`,
																	}}>
																	<EllipsisText
																		text={cell.label}
																		maxLines={4}
																		lineHeight={textStyle.lineHeight} // tracks font size
																		fontSize={pxToRem(textStyle.fontSize)} // inline style, not Tailwind class
																		className="text-center w-full"
																	/>
																</div>
															);
														})}
													</div>
												);
											})}

										{/* ── Attribute label row ────────────────────────────────── */}
										<div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
											{showAxisColumn && <div className="sticky left-0 z-20 bg-white" style={{ borderRight: `1px solid ${colorsConfig.borders.solid}` }} />}
											{flatColumns.map((col, idx) => {
												const bgColor = idx % 2 === 1 ? colorsConfig.backgrounds.alternate : colorsConfig.backgrounds.white;
												const borders = getBorders("attrLabel", 0, idx);
												return (
													<div
														key={idx}
														className="flex items-center justify-center"
														style={{
															backgroundColor: bgColor,
															color: colorsConfig.text.tertiary,
															/* ← SCALED: tighter padding; fewer lines */
															padding: `${scaled.geometry.cellPaddingV}px ${scaled.geometry.cellPaddingH}px`,
															minHeight: pxToRem(scaled.layout.headerRowHeight),
															...borders,
														}}>
														<EllipsisText
															text={col.attribute}
															maxLines={4}
															lineHeight={scaled.typography.axisLabel + 4}
															fontSize={pxToRem(scaled.typography.axisLabel)}
															className="font-normal text-center w-full"
														/>
													</div>
												);
											})}
										</div>

										{/* ── N-size row ─────────────────────────────────────────── */}
										<div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
											{showAxisColumn && <div className="sticky left-0 z-20 bg-white" style={{ borderRight: `1px solid ${colorsConfig.borders.solid}` }} />}
											{flatColumns.map((col, idx) => {
												const bgColor = idx % 2 === 1 ? colorsConfig.backgrounds.alternate : colorsConfig.backgrounds.white;
												const borders = getBorders("nsize", 0, idx);
												return (
													<div
														key={idx}
														className="flex items-center justify-center"
														style={{
															backgroundColor: bgColor,
															/* ← SCALED: n-size row height */
															padding: `${Math.max(1, Math.floor(scaled.geometry.cellPaddingV / 2))}px 0`,
															minHeight: pxToRem(scaled.layout.nSizeRowHeight),
															...borders,
														}}>
														<NSizeLabel nSize={col.n_size} style={{ color: colorsConfig.text.tertiary }} fontSize={scaled.typography.nSize} />
													</div>
												);
											})}
										</div>
									</div>

									{/* ── Chart area ──────────────────────────────────────────── */}
									<div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
										{showAxisColumn && (
											<div
												className="sticky left-0 z-20 bg-white flex flex-col justify-end"
												style={{ height: dynamicChartHeight, borderRight: `1px solid ${colorsConfig.borders.solid}` }}>
												<YAxisScale
													height={dynamicChartHeight}
													paddingTop={scaled.geometry.gridTop}
													paddingBottom={
														chartBottomPadding > 0
															? chartBottomPadding
															: needsScroll
																? layoutConfig.axis.bottom + spacingConfig.padding.buffer / 2
																: layoutConfig.axis.bottom + spacingConfig.padding.buffer / 3
													}
													domain={yAxisDomain}
													labelFormat={labelFormat}
													formatDataLabel={formatDataLabel}
													fontSize={pxToRem(scaled.typography.axisLabel)}
												/>
											</div>
										)}

										{flatColumns.map((col, idx) => {
											const filteredSeries = col.seriesData.filter((s: any) => visibleWaves?.includes(s.name));
											const bgColor = idx % 2 === 1 ? colorsConfig.backgrounds.alternate : colorsConfig.backgrounds.white;
											const borders = getBorders("data", 0, idx);
											return (
												<div key={idx} className="flex justify-center px-1 items-end" style={{ height: dynamicChartHeight, backgroundColor: bgColor, ...borders }}>
													{filteredSeries.length > 0 && (
													<MiniVBarChart
														key={filteredSeries.map((s: any) => s.value).join("-")}
														series={filteredSeries}
														waves={chartResponse?.chart_config?.ser_color}
														width={actualColumnWidth}
														labelFormat={labelFormat}
														yAxisDomain={yAxisDomain}
														height={dynamicChartHeight}
														top={scaled.geometry.gridTop} /* ← SCALED */
														bottom={chartBottomPadding}
														barWidth={barWidth}
															dataLabelFontSize={dataLabelFontSize} /* ← SCALED */
															isUltraCompact={isUltraCompact}
															statTestConfig={statTestConfig}
															renderedFrom={renderedFrom}
														/>
													)}
												</div>
											);
										})}
									</div>
								</div>
							</div>
						</>
					) : (
						// Invisible placeholder — holds flex space while height resolves
						<div className="w-full h-full" />
					)}
				</div>
			) : (
				<div
					ref={tableViewContainerRef}
					style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", justifyContent: "center", alignItems: "center" }}>
					<ChartTabularView
						chartData={apiData}
						chartType="v_stacked_column"
						tableContainerHeight={tableViewContainerHeight}
						parentContainerWidth={containerWidth}
						labelFormat={labelFormat}
						statTestConfig={statTestConfig}
						renderedFrom={renderedFrom}
						filterHierarchy={hierarchyDef}
						seriesOrder={seriesOrder}
					/>
				</div>
			)}
			{/* X-Axis Label */}
			{
				isChartView && chartResponse?.x_axis_label && !isEffectiveMultiTile && (
					<AxisLabel
						label={chartResponse?.chart_config?.x_lbl || chartResponse?.x_axis_label}
						orientation="horizontal"
						className="mt-2"
					/>
				)
			}

			<div ref={footnoteRef}>
				<ChartFootnote
					src={chartResponse.footnote?.src}
					footnoteObj={chartResponse.footnote}
					analysis_name={chartResponse.analysis_type}
					hasLowNSize={showLowNSize}
					isWeighted={chartResponse.is_weight}
					className="py-3 mt-auto"
					stat_message={chartResponse.footnote?.stat_message}
					valueParams={chartResponse?.value_params}
					renderedFrom={renderedFrom}
				/>
			</div>
		</ChartContainer>
	);
};

export default VClusteredBarChart;
